import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'

import { apiLogDirection, listExternalApiLog, type ExternalApiLogRow } from '@/api/external-api-log'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { emptyTableCellClassName } from '@/constants/ui'
import { dateFnsLocale } from '@/utils/dateLocale'

/** Фильтр направления: все / входящие заявки (avito-lead) / опросы статуса (avito-stats). */
type DirectionFilter = 'all' | 'avito-lead' | 'avito-stats'

/**
 * /avito-api-log — единый журнал запросов между Avito и нами. Источник:
 * `public.external_api_log`, миграция 20260602100000.
 *
 * Покрывает оба эндпойнта:
 *  - `avito-lead`  — входящие заявки (Avito → мы, вебхук);
 *  - `avito-stats` — исходящие опросы статусов (Avito спрашивает — мы отвечаем).
 *
 * Видно: дата, направление, статус ответа, reason, lead_id (кликабельный →
 * карточка лида), IP, длительность обработки, user-agent. Фильтр по
 * направлению и по lead_id (если разбираемся по конкретной заявке).
 */
export function AvitoApiLogPage() {
  const { t } = useTranslation()
  const [leadIdFilter, setLeadIdFilter] = useState('')
  const [filterApplied, setFilterApplied] = useState('')
  const [direction, setDirection] = useState<DirectionFilter>('all')

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['external-api-log', 'avito', direction, filterApplied || null],
    queryFn: () =>
      listExternalApiLog({
        partner: 'avito',
        endpoint: direction === 'all' ? undefined : direction,
        leadId: filterApplied || undefined,
        limit: 200,
      }),
    refetchInterval: 10_000,
  })

  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <PageHeader title={t('logs.avito.title')} />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <SegmentedControl<DirectionFilter>
          aria-label={t('logs.avito.directionLabel')}
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'all', label: t('logs.avito.dirAll') },
            { value: 'avito-lead', label: t('logs.avito.dirIncoming') },
            { value: 'avito-stats', label: t('logs.avito.dirStatusPoll') },
          ]}
        />
        <div className="w-[420px] max-w-full">
          <Input
            label={t('logs.avito.leadIdFilter')}
            value={leadIdFilter}
            onChange={(e) => setLeadIdFilter(e.target.value)}
            placeholder="427031da-4f75-4a5c-aa6d-4199a49c1af0"
            hint={t('logs.avito.leadIdHint')}
          />
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setFilterApplied(leadIdFilter.trim())}
          disabled={isFetching}
        >
          {t('logs.avito.show')}
        </Button>
        {filterApplied ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setLeadIdFilter('')
              setFilterApplied('')
            }}
          >
            {t('common.reset')}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? t('logs.avito.refreshing') : t('logs.avito.refresh')}
        </Button>
      </div>

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {(error as Error).message}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.avito.colWhen')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.avito.colDirection')}</th>
              <th className="px-3 py-2 whitespace-nowrap">HTTP</th>
              <th className="px-3 py-2 whitespace-nowrap">reason</th>
              <th className="px-3 py-2 whitespace-nowrap">lead_id</th>
              <th className="px-3 py-2 whitespace-nowrap">IP</th>
              <th className="px-3 py-2 whitespace-nowrap">User-Agent</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('logs.avito.colMs')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className={emptyTableCellClassName}>
                  <Loader centered size="sm" />
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan={8} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              data.map((row) => <LogRow key={row.id} row={row} />)
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
        {t('logs.avito.footer')}
      </p>
    </div>
  )
}

function LogRow({ row }: { row: ExternalApiLogRow }) {
  const { t } = useTranslation()
  const at = format(new Date(row.called_at), "d MMM HH:mm:ss", { locale: dateFnsLocale() })
  const dir = apiLogDirection(row.endpoint)
  const dirLabel =
    dir.kind === 'in'
      ? t('logs.avito.badgeIn')
      : dir.kind === 'out'
        ? t('logs.avito.badgeOut')
        : dir.label
  const dirColor =
    dir.kind === 'in'
      ? 'border-blue-500/40 bg-blue-500/10 text-blue-300'
      : dir.kind === 'out'
        ? 'border-purple-500/40 bg-purple-500/10 text-purple-300'
        : 'border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-bg-elev)] text-[color:var(--stryzh-color-text-secondary)]'
  const httpColor =
    row.status_code >= 500
      ? 'text-red-300'
      : row.status_code >= 400
        ? 'text-yellow-300'
        : 'text-green-300'
  return (
    <tr className="border-t border-[color:var(--stryzh-color-border-default)]">
      <td className="px-3 py-2 whitespace-nowrap font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-secondary)]">
        {at}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${dirColor}`}
          title={row.endpoint}
        >
          {dirLabel}
        </span>
      </td>
      <td className={`px-3 py-2 whitespace-nowrap font-[family-name:var(--stryzh-font-family-mono)] font-semibold ${httpColor}`}>
        {row.status_code}
      </td>
      <td className="px-3 py-2 whitespace-nowrap font-[family-name:var(--stryzh-font-family-mono)] text-xs">
        {row.reason ?? '—'}
      </td>
      <td className="px-3 py-2 max-w-[280px] truncate font-[family-name:var(--stryzh-font-family-mono)] text-xs">
        {row.lead_id ? (
          <Link
            to={`/inspection-leads/${row.lead_id}`}
            className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
            title={row.lead_id}
          >
            {row.lead_id.slice(0, 8)}…{row.lead_id.slice(-4)}
          </Link>
        ) : (
          <span className="text-[color:var(--stryzh-color-text-secondary)]">—</span>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap font-[family-name:var(--stryzh-font-family-mono)] text-xs">
        {row.remote_ip ?? '—'}
      </td>
      <td className="px-3 py-2 max-w-[300px] truncate text-xs text-[color:var(--stryzh-color-text-secondary)]" title={row.user_agent ?? ''}>
        {row.user_agent ?? '—'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap font-[family-name:var(--stryzh-font-family-mono)] text-xs text-[color:var(--stryzh-color-text-secondary)]">
        {row.duration_ms != null ? row.duration_ms : '—'}
      </td>
    </tr>
  )
}
